import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../../../utilities/api';
import { LINK_ERROR_MESSAGES } from './linkErrorMessages';

// TODO: confirm final copy with design/content for each of these.
// Keys below are literal error-code strings the backend returns as array values (not JSON keys
// subject to camelCase inflection), so they must stay snake_case to match.
/* eslint-disable camelcase */
const VERIFY_ERROR_MESSAGES = {
  code_expired_or_not_found: {
    title: 'That code has expired',
    body: 'Request a new code below to continue.',
  },
  incorrect_code: {
    title: 'That code doesn’t match',
    body: 'Each code only works once. Request a new code below and try again.',
  },
  ...LINK_ERROR_MESSAGES,
};
/* eslint-enable camelcase */

const DEFAULT_ERROR_MESSAGE = {
  title: 'Something went wrong',
  body: 'We couldn’t verify your code. Try requesting a new code.',
};

const validateCode = value => {
  if (!value) return 'Please enter the code we sent you';
  if (!/^\d{6}$/.test(value)) return 'Your code should be 6 digits';
  return null;
};

/**
 * Second step of the PIV self-service claim flow: the user enters the one-time code sent to the
 * email GCLAWS has on file. Each code works for one attempt only, so a wrong or expired code
 * always means requesting a new one ("Send another code"), not retrying the same code.
 */
const CodeEntryStep = ({
  registrationNumber,
  individualType,
  maskedEmail,
  onLinked,
}) => {
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [apiError, setApiError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);

  const handleVerify = async () => {
    const validationError = validateCode(code);
    if (validationError) {
      setFieldError(validationError);
      return;
    }
    setFieldError('');
    setApiError(null);
    setCodeResent(false);
    setIsVerifying(true);

    try {
      await api.verifyPivClaimCode(code);
      onLinked();
    } catch (err) {
      if (err instanceof Response) {
        const body = await err.json().catch(() => ({}));
        const [errorCode] = body?.errors || [];
        setApiError(VERIFY_ERROR_MESSAGES[errorCode] || DEFAULT_ERROR_MESSAGE);
      } else {
        setApiError(DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setFieldError('');
    setApiError(null);
    setCodeResent(false);
    setIsResending(true);

    try {
      const response = await api.requestPivClaimCode(
        registrationNumber,
        individualType || undefined,
      );
      const body = await response.json().catch(() => ({}));
      // Auto-link can succeed on a resend too, in which case no code was sent.
      if (body.linked) {
        onLinked();
        return;
      }
      setCode('');
      setAttemptsRemaining(
        Number.isInteger(body.attemptsRemaining)
          ? body.attemptsRemaining
          : null,
      );
      setCodeResent(true);
    } catch (err) {
      if (err instanceof Response && err.status === 429) {
        setApiError({
          title: 'Too many attempts',
          body: 'You’ve requested too many codes. Wait a while before trying again.',
        });
      } else if (err instanceof Response) {
        const body = await err.json().catch(() => ({}));
        const [errorCode] = body?.errors || [];
        setApiError(LINK_ERROR_MESSAGES[errorCode] || DEFAULT_ERROR_MESSAGE);
      } else {
        setApiError(DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setIsResending(false);
    }
  };

  const resentMessage =
    attemptsRemaining === null
      ? 'Another code was sent.'
      : `Another code was sent. (${attemptsRemaining} ${
          attemptsRemaining === 1 ? 'attempt' : 'attempts'
        } remaining.)`;

  return (
    <>
      <h1 className="dashboard__header">Set up your PIV card</h1>
      <p data-dd-privacy="mask">
        {`A 6-digit verification code was sent to ${maskedEmail}. To pair this email with your PIV card, enter the code below. You will then be able to sign in to ARP with your PIV card at any time.`}
      </p>
      <va-text-input
        data-dd-privacy="mask"
        class="vads-u-margin-top--3"
        label="6-digit verification code"
        name="pivClaimCode"
        value={code}
        inputmode="numeric"
        maxlength="6"
        onInput={e => setCode(e.target.value)}
        required
        error={fieldError}
        data-testid="piv-claim-code-input"
        autocomplete="one-time-code"
        show-input-error
      />
      <div className="vads-u-display--flex vads-u-margin-top--4">
        <va-button
          big
          onClick={handleVerify}
          text="Verify your PIV"
          data-testid="piv-claim-verify-code-button"
          uswds
          loading={isVerifying}
        />
        <va-button
          secondary
          onClick={handleResend}
          text="Send another code"
          data-testid="piv-claim-resend-code-button"
          uswds
          loading={isResending}
        />
      </div>
      <p
        className="vads-u-margin-top--2"
        data-testid="piv-claim-code-resent-confirmation"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {codeResent ? resentMessage : ''}
      </p>
      {apiError && (
        <va-alert
          status="error"
          visible
          class="vads-u-margin-top--3"
          data-testid="piv-claim-code-error-alert"
        >
          <h2 slot="headline">{apiError.title}</h2>
          <p className="vads-u-margin-y--0">{apiError.body}</p>
        </va-alert>
      )}
    </>
  );
};

CodeEntryStep.propTypes = {
  maskedEmail: PropTypes.string.isRequired,
  registrationNumber: PropTypes.string.isRequired,
  onLinked: PropTypes.func.isRequired,
  individualType: PropTypes.string,
};

CodeEntryStep.defaultProps = {
  individualType: undefined,
};

export default CodeEntryStep;
